export type SupportRoutingRulesFormState = {
  ignoreAddresses: string[];
  doNotReplyAddresses: string[];
  alertGroupAddresses: string[];
  alertGroupWindowHours: number;
  afterHoursAction: 'Process' | 'AutoReply' | 'Defer';
  afterHoursAutoReplyMessage: string;
  businessHoursCalendarId: string;
  /** Campos não cobertos pelo formulário (ex.: ignoreContent, keywordCatalogRules). */
  extras: Record<string, unknown>;
};

const KNOWN_KEYS = new Set([
  'ignoreAddresses',
  'doNotReplyAddresses',
  'alertGroupAddresses',
  'alertGroupWindowHours',
  'afterHoursAction',
  'afterHoursAutoReplyMessage',
  'businessHoursCalendarId',
]);

const DEFAULT_WINDOW_HOURS = 24;

function asStringList(value: unknown): string[] {
  if (!Array.isArray(value)) {
    if (typeof value === 'string' && value.trim()) {
      return [value.trim()];
    }
    return [];
  }

  return value
    .map((item) => (item == null ? '' : String(item).trim()))
    .filter(Boolean);
}

function asAfterHoursAction(value: unknown): SupportRoutingRulesFormState['afterHoursAction'] {
  const raw = typeof value === 'string' ? value.trim() : '';
  if (raw === 'AutoReply' || raw === 'Defer' || raw === 'Process') {
    return raw;
  }
  return 'Process';
}

function asWindowHours(value: unknown): number {
  const parsed = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(parsed) || parsed <= 0) {
    return DEFAULT_WINDOW_HOURS;
  }
  return Math.min(168, Math.round(parsed));
}

export function emptySupportRoutingRulesFormState(): SupportRoutingRulesFormState {
  return {
    ignoreAddresses: [],
    doNotReplyAddresses: [],
    alertGroupAddresses: [],
    alertGroupWindowHours: DEFAULT_WINDOW_HOURS,
    afterHoursAction: 'Process',
    afterHoursAutoReplyMessage: '',
    businessHoursCalendarId: '',
    extras: {},
  };
}

export function parseSupportRoutingRulesJson(json?: string | null): SupportRoutingRulesFormState {
  const empty = emptySupportRoutingRulesFormState();
  if (!json?.trim()) {
    return empty;
  }

  try {
    const parsed = JSON.parse(json) as unknown;
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return empty;
    }

    const record = parsed as Record<string, unknown>;
    const extras: Record<string, unknown> = {};
    for (const [key, value] of Object.entries(record)) {
      if (!KNOWN_KEYS.has(key)) {
        extras[key] = value;
      }
    }

    return {
      ignoreAddresses: asStringList(record.ignoreAddresses),
      doNotReplyAddresses: asStringList(record.doNotReplyAddresses),
      alertGroupAddresses: asStringList(record.alertGroupAddresses),
      alertGroupWindowHours: asWindowHours(record.alertGroupWindowHours),
      afterHoursAction: asAfterHoursAction(record.afterHoursAction),
      afterHoursAutoReplyMessage:
        typeof record.afterHoursAutoReplyMessage === 'string'
          ? record.afterHoursAutoReplyMessage
          : '',
      businessHoursCalendarId:
        typeof record.businessHoursCalendarId === 'string'
          ? record.businessHoursCalendarId.trim()
          : '',
      extras,
    };
  } catch {
    return empty;
  }
}

export function serializeSupportRoutingRulesJson(state: SupportRoutingRulesFormState): string {
  const payload: Record<string, unknown> = { ...state.extras };

  const ignoreAddresses = state.ignoreAddresses.map((item) => item.trim()).filter(Boolean);
  const doNotReplyAddresses = state.doNotReplyAddresses.map((item) => item.trim()).filter(Boolean);
  const alertGroupAddresses = state.alertGroupAddresses.map((item) => item.trim()).filter(Boolean);

  if (ignoreAddresses.length > 0) {
    payload.ignoreAddresses = ignoreAddresses;
  } else {
    delete payload.ignoreAddresses;
  }

  if (doNotReplyAddresses.length > 0) {
    payload.doNotReplyAddresses = doNotReplyAddresses;
  } else {
    delete payload.doNotReplyAddresses;
  }

  if (alertGroupAddresses.length > 0) {
    payload.alertGroupAddresses = alertGroupAddresses;
  } else {
    delete payload.alertGroupAddresses;
  }

  if (state.alertGroupWindowHours !== DEFAULT_WINDOW_HOURS) {
    payload.alertGroupWindowHours = asWindowHours(state.alertGroupWindowHours);
  } else if (alertGroupAddresses.length > 0 || doNotReplyAddresses.length > 0) {
    payload.alertGroupWindowHours = DEFAULT_WINDOW_HOURS;
  } else {
    delete payload.alertGroupWindowHours;
  }

  if (state.afterHoursAction !== 'Process') {
    payload.afterHoursAction = state.afterHoursAction;
  } else {
    delete payload.afterHoursAction;
  }

  const autoReply = state.afterHoursAutoReplyMessage.trim();
  if (autoReply) {
    payload.afterHoursAutoReplyMessage = autoReply;
  } else {
    delete payload.afterHoursAutoReplyMessage;
  }

  const calendarId = state.businessHoursCalendarId.trim();
  if (calendarId) {
    payload.businessHoursCalendarId = calendarId;
  } else {
    delete payload.businessHoursCalendarId;
  }

  return Object.keys(payload).length === 0 ? '' : JSON.stringify(payload);
}
